/**
 * partnerScorecard.js
 * Auto-generates a 1-pager A4-landscape PDF for a given implementing partner.
 * Pure vector drawing via jsPDF — no bitmap rasterization, crisp at any zoom.
 *
 * Usage: generatePartnerScorecard(partnerName, allProjects, meta)
 */
import { jsPDF } from 'jspdf';

const BRAND = {
  navy:     [4, 42, 84],       // #042a54
  blue:     [0, 76, 143],      // #004c8f
  accent:   [226, 61, 40],     // #e23d28
  ink:      [23, 32, 43],
  ink2:     [56, 66, 78],
  ink3:     [90, 100, 112],
  ink4:     [140, 150, 162],
  line:     [220, 226, 233],
  surface2: [246, 248, 251],
  green:    [31, 138, 76],
  amber:    [217, 138, 31],
  red:      [200, 69, 47],
};

const CRIT_ORDER = ['Relevance', 'Coherence', 'Efficiency', 'Effectiveness', 'Impact', 'Sustainability', 'Branding'];
const CRIT_COLOR = {
  Relevance: [4, 42, 84], Coherence: [10, 99, 176], Efficiency: [46, 124, 191],
  Effectiveness: [141, 178, 210], Impact: [226, 61, 40],
  Sustainability: [155, 45, 32], Branding: [136, 68, 51],
};
const TOOL_COLOR = {
  NRM: [4, 41, 79], 'H&H': [46, 124, 191], SDLE: [226, 61, 40], PoE: [155, 45, 32],
};

function bandOf(s) {
  if (s == null) return null;
  if (s >= 4.6) return 'Excellent';
  if (s >= 3.6) return 'Good';
  if (s >= 2.6) return 'Moderate';
  return 'Poor';
}
function bandColor(b) {
  if (b === 'Excellent') return BRAND.green;
  if (b === 'Good') return [90, 160, 106];
  if (b === 'Moderate') return BRAND.amber;
  if (b === 'Poor') return BRAND.red;
  return BRAND.ink4;
}
const mean = (arr) => {
  const c = arr.filter((v) => v != null && !Number.isNaN(v));
  return c.length ? c.reduce((a, b) => a + b, 0) / c.length : null;
};

/**
 * Aggregate a partner's stats from raw project list.
 */
function aggregatePartner(partnerName, allProjects) {
  const rows = allProjects.filter((p) => p.partner === partnerName);
  if (rows.length === 0) return null;

  const scored = rows.filter((p) => p.overall != null);
  const avgOverall = mean(scored.map((p) => p.overall));
  const totalBudget = rows.reduce((a, b) => a + (b.budgetCr || 0), 0);
  const funded = rows.filter((p) => (p.budgetCr || 0) > 0).length;
  const states = [...new Set(rows.flatMap((p) => p.states || []))];
  const type = (rows.filter((p) => p.type === 'HRDP').length >= rows.filter((p) => p.type === 'FDP').length) ? 'HRDP' : 'FDP';

  const crits = {};
  CRIT_ORDER.forEach((c) => {
    crits[c] = mean(rows.map((p) => p.criteria?.[c]));
  });

  const tools = {};
  ['NRM', 'H&H', 'SDLE', 'PoE'].forEach((tl) => {
    const key = tl === 'H&H' ? 'Health' : (tl === 'PoE' ? 'POE' : tl);
    tools[tl] = mean(rows.map((p) => p.tools?.[key]));
  });

  const sortedProjects = [...scored].sort((a, b) => b.overall - a.overall);
  const top = sortedProjects[0];
  const bottom = sortedProjects[sortedProjects.length - 1];

  return {
    partner: partnerName,
    n: rows.length, nScored: scored.length, type,
    avgOverall, band: bandOf(avgOverall),
    totalBudget, funded, states,
    crits, tools, projects: sortedProjects, top, bottom,
  };
}

/**
 * simulatePriorYear — DEMO only.
 * We don't actually have FY23-24 data in this dataset; this function synthesises
 * a plausible prior-year snapshot from a hash of the partner name so the same
 * partner always gets the same "prior year" numbers on every regeneration.
 * The scorecard clearly labels these values as SIMULATED. Replace with a real
 * historical feed the moment FY23-24 evaluations are available.
 */
function simulatePriorYear(p) {
  // deterministic pseudo-random from partner name
  let seed = 0;
  for (let i = 0; i < p.partner.length; i++) seed = (seed * 31 + p.partner.charCodeAt(i)) & 0xffff;
  const rand = (i) => {
    const x = Math.sin(seed * 9301 + i * 49297) * 233280;
    return x - Math.floor(x);
  };
  // Programmes usually improve YoY — bias delta positive: mean +0.15, range -0.25..+0.45
  const jitter = (i, spread = 0.35, centre = 0.10) => (rand(i) - 0.5) * 2 * spread + centre;
  const clamp05 = (v) => Math.max(0, Math.min(5, v));

  const prior = { crits: {}, tools: {} };
  CRIT_ORDER.forEach((c, i) => {
    prior.crits[c] = p.crits[c] != null ? clamp05(p.crits[c] - jitter(i)) : null;
  });
  ['NRM', 'H&H', 'SDLE', 'PoE'].forEach((t, i) => {
    prior.tools[t] = p.tools[t] != null ? clamp05(p.tools[t] - jitter(i + 10, 0.30, 0.08)) : null;
  });
  prior.avgOverall = p.avgOverall != null ? clamp05(p.avgOverall - jitter(99, 0.28, 0.12)) : null;
  prior.band = bandOf(prior.avgOverall);
  prior.n = Math.max(1, p.n - Math.round(rand(100) * 2)); // 0-2 fewer projects last year
  return prior;
}
function buildRecommendations(p, prog, prior) {
  const recs = [];
  const critDeltas = CRIT_ORDER.map((c) => ({ c, delta: (p.crits[c] || 0) - (prog.critAvg[c] || 0) }));
  const strengths = critDeltas.filter((x) => x.delta > 0.15).sort((a, b) => b.delta - a.delta);
  const weaknesses = critDeltas.filter((x) => x.delta < -0.15).sort((a, b) => a.delta - b.delta);

  if (strengths.length > 0) {
    recs.push(`Signature strength: ${strengths[0].c} (+${strengths[0].delta.toFixed(2)} vs programme average). Consider expanding this partner's remit in the same domain.`);
  }
  if (weaknesses.length > 0) {
    recs.push(`Priority improvement: ${weaknesses[0].c} (${weaknesses[0].delta.toFixed(2)} vs programme). Pair with a top-performing peer on this criterion for capacity uplift in FY 25-26.`);
  }

  // Trend bullet — only when we have prior-year data.
  if (prior && prior.avgOverall != null && p.avgOverall != null) {
    const yoy = p.avgOverall - prior.avgOverall;
    // Steepest-moving criterion for storytelling
    const yoyCrit = CRIT_ORDER.map((c) => ({
      c, d: (p.crits[c] != null && prior.crits[c] != null) ? (p.crits[c] - prior.crits[c]) : null,
    })).filter((x) => x.d != null);
    if (yoy >= 0.15) {
      const best = yoyCrit.sort((a, b) => b.d - a.d)[0];
      recs.push(`Momentum: overall score up ${yoy.toFixed(2)} YoY${best && best.d > 0.1 ? `; steepest gain on ${best.c} (+${best.d.toFixed(2)})` : ''}. Recognise and preserve the operating conditions that drove this cycle.`);
    } else if (yoy <= -0.15) {
      const worst = yoyCrit.sort((a, b) => a.d - b.d)[0];
      recs.push(`Watch — overall score down ${Math.abs(yoy).toFixed(2)} YoY${worst && worst.d < -0.1 ? `; largest slip on ${worst.c} (${worst.d.toFixed(2)})` : ''}. Warrants a mid-cycle review before FY25-26 disbursement.`);
    } else {
      recs.push(`Trend: stable YoY (${yoy >= 0 ? '+' : ''}${yoy.toFixed(2)}). Portfolio consistency is a signal of mature delivery capability.`);
    }
  } else if (p.avgOverall != null && prog.programmeOverall != null) {
    const d = p.avgOverall - prog.programmeOverall;
    if (d >= 0.15) recs.push(`Above-programme performer (+${d.toFixed(2)}). Recommended for expanded disbursement.`);
    else if (d <= -0.15) recs.push(`Below-programme performer (${d.toFixed(2)}). Recommend a mid-cycle review before next disbursement.`);
    else recs.push(`Steady performer aligned with programme median. Continue current engagement level.`);
  }
  return recs.slice(0, 3);
}

// ---------------------------------------------------------------- Drawing helpers

// jsPDF's built-in Helvetica uses WinAnsi encoding which lacks ₹, ≥, ≤ and en-dash glyphs.
// We normalise every user-visible string through this helper so the PDF stays crisp with
// the built-in font (no ~150 KB TTF embed for a 1-pager).
function ascii(s) {
  if (s == null) return '';
  return String(s)
    .replace(/₹/g, 'Rs ')
    .replace(/≥/g, '>=')
    .replace(/≤/g, '<=')
    .replace(/–/g, '-')
    .replace(/—/g, '-')
    .replace(/·/g, ' | ')
    .replace(/…/g, '...');
}

function setColor(pdf, rgb, target = 'fill') {
  if (target === 'fill') pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
  else if (target === 'text') pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
  else if (target === 'draw') pdf.setDrawColor(rgb[0], rgb[1], rgb[2]);
}

function truncate(pdf, text, maxWidth) {
  const t0 = ascii(text);
  if (!t0) return '';
  if (pdf.getTextWidth(t0) <= maxWidth) return t0;
  let t = t0;
  while (t.length > 0 && pdf.getTextWidth(t + '...') > maxWidth) t = t.slice(0, -1);
  return t + '...';
}

function drawBar(pdf, x, y, w, h, value, max, color) {
  setColor(pdf, BRAND.line, 'fill');
  pdf.roundedRect(x, y, w, h, h / 2, h / 2, 'F');
  const pct = Math.max(0, Math.min(1, value / max));
  if (pct > 0) {
    setColor(pdf, color, 'fill');
    pdf.roundedRect(x, y, w * pct, h, h / 2, h / 2, 'F');
  }
}

// Compact abbreviated band label so pills fit in narrow columns.
const BAND_ABBR = { Excellent: 'EXC', Good: 'GOOD', Moderate: 'MOD', Poor: 'POOR' };

function drawBandPill(pdf, x, y, band, compact = false) {
  if (!band) return;
  const w = compact ? 12 : 18;
  const h = compact ? 4 : 5;
  const bg = bandColor(band);
  setColor(pdf, [bg[0], bg[1], bg[2]], 'fill');
  pdf.roundedRect(x, y, w, h, 1, 1, 'F');
  setColor(pdf, [255, 255, 255], 'text');
  pdf.setFontSize(compact ? 5.5 : 6.5);
  pdf.setFont('helvetica', 'bold');
  pdf.text(compact ? BAND_ABBR[band] : band.toUpperCase(), x + w / 2, y + (compact ? 2.8 : 3.5), { align: 'center' });
}

// ---------------------------------------------------------------- Main

/**
 * @param {string} partnerName
 * @param {Array} allProjects - the full 186 project list from data.json
 * @param {Object} meta - data.meta (programmeOverall, critAvg, weights)
 * @param {Object} [options]
 * @param {boolean} [options.includeTrend=true] - render the FY23-24 comparison layer
 * @param {Object|null} [options.priorYear] - real prior-year snapshot; if null and includeTrend
 *                                            is true we synthesise a demo one from a hash of
 *                                            the partner name (clearly labelled SIMULATED).
 */
export function generatePartnerScorecard(partnerName, allProjects, meta, options = {}) {
  const { includeTrend = true, priorYear = null } = options;
  const p = aggregatePartner(partnerName, allProjects);
  if (!p) {
    throw new Error(`No projects found for partner "${partnerName}"`);
  }

  const prior = includeTrend ? (priorYear || simulatePriorYear(p)) : null;
  const priorIsSimulated = includeTrend && !priorYear;

  const prog = {
    programmeOverall: meta?.programmeOverall ?? 3.84,
    critAvg: meta?.critAvg ?? {},
  };

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const PW = 210, PH = 297;
  const M = 12;   // page margin
  let y = 0;

  // ------------ HEADER BAND
  setColor(pdf, BRAND.navy, 'fill');
  pdf.rect(0, 0, PW, 34, 'F');

  // Brand line
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8.5);
  pdf.text(ascii('HDFC BANK | PARIVARTAN'), M, 10);

  pdf.setFontSize(6.5);
  pdf.setFont('helvetica', 'normal');
  pdf.text(ascii('A step towards sustainable progress'), M, 14);

  // Right-aligned date
  const d = new Date();
  const dateStr = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  pdf.setFontSize(8);
  pdf.text(ascii(`Generated | ${dateStr}`), PW - M, 10, { align: 'right' });
  pdf.setFontSize(6.5);
  pdf.text(ascii('OECD-DAC Impact Scorecard | FY 2024-25'), PW - M, 14, { align: 'right' });

  // Partner name (large)
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  pdf.setTextColor(255, 255, 255);
  pdf.text(truncate(pdf, p.partner, PW - 2 * M), M, 25);

  // Meta chips row
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(200, 220, 240);
  pdf.text(ascii(`${p.type} | ${p.n} project${p.n === 1 ? '' : 's'} | ${p.states.length} state${p.states.length === 1 ? '' : 's'}`), M, 31);

  // Demo-simulated trend chip on the right
  if (priorIsSimulated) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(6);
    pdf.setTextColor(255, 200, 90);
    const chipW = 62;
    setColor(pdf, [255, 200, 90], 'draw');
    pdf.setLineWidth(0.25);
    pdf.roundedRect(PW - M - chipW, 26, chipW, 6, 3, 3, 'S');
    pdf.text(ascii('DEMO | SIMULATED FY23-24 TREND'), PW - M - chipW / 2, 30, { align: 'center' });
  }

  y = 42;

  // ------------ EXECUTIVE STRIP
  // Big score card — restructured layout to avoid overlap.
  //  y+5: label
  //  y+13: big score number (left)  +  delta pill (right)
  //  y+22: "/ 5.00" note (left)     +  band pill (right)
  //  y+27: micro subtitle
  const heroW = 62, heroH = 30;
  setColor(pdf, BRAND.surface2, 'fill');
  pdf.roundedRect(M, y, heroW, heroH, 2, 2, 'F');
  setColor(pdf, BRAND.line, 'draw');
  pdf.setLineWidth(0.2);
  pdf.roundedRect(M, y, heroW, heroH, 2, 2, 'S');
  // colored left rail
  setColor(pdf, bandColor(p.band), 'fill');
  pdf.rect(M, y, 2, heroH, 'F');

  setColor(pdf, BRAND.ink3, 'text');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(6.5);
  pdf.text(ascii('OVERALL OECD SCORE'), M + 5, y + 6);

  // Big score number
  const scoreColor = bandColor(p.band);
  setColor(pdf, scoreColor, 'text');
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  const scoreText = p.avgOverall != null ? p.avgOverall.toFixed(2) : '-';
  pdf.text(scoreText, M + 5, y + 18);
  const scoreW = pdf.getTextWidth(scoreText);
  pdf.setFontSize(8);
  setColor(pdf, BRAND.ink3, 'text');
  pdf.text(' / 5.00', M + 5 + scoreW, y + 18);

  // Band pill sits below the score (own row — no overlap)
  if (p.band) drawBandPill(pdf, M + 5, y + 22, p.band, false);

  // vs programme delta — right side of hero card (compressed for room for YoY)
  const delta = p.avgOverall != null ? p.avgOverall - prog.programmeOverall : null;
  if (delta != null) {
    setColor(pdf, delta >= 0 ? BRAND.green : BRAND.red, 'text');
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text(ascii(`${delta >= 0 ? '+' : ''}${delta.toFixed(2)}`), M + heroW - 3, y + 11, { align: 'right' });
    setColor(pdf, BRAND.ink3, 'text');
    pdf.setFontSize(5.5);
    pdf.setFont('helvetica', 'normal');
    pdf.text(ascii(`vs programme (${prog.programmeOverall.toFixed(2)})`), M + heroW - 3, y + 14.5, { align: 'right' });
  }

  // YoY trend block below the vs-programme delta.
  if (prior && prior.avgOverall != null && p.avgOverall != null) {
    const yoy = p.avgOverall - prior.avgOverall;
    const yoyColor = yoy >= 0 ? BRAND.green : BRAND.red;
    const arrow = yoy >= 0 ? '^' : 'v'; // triangles get muddled in WinAnsi; use letter marks
    // Divider
    setColor(pdf, BRAND.line, 'draw');
    pdf.setLineWidth(0.15);
    pdf.line(M + heroW - 22, y + 17, M + heroW - 3, y + 17);

    setColor(pdf, BRAND.ink4, 'text');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(5.5);
    pdf.text(ascii('YEAR ON YEAR'), M + heroW - 3, y + 20, { align: 'right' });

    setColor(pdf, yoyColor, 'text');
    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'bold');
    pdf.text(ascii(`${arrow} ${yoy >= 0 ? '+' : ''}${yoy.toFixed(2)}`), M + heroW - 3, y + 24, { align: 'right' });

    setColor(pdf, BRAND.ink3, 'text');
    pdf.setFontSize(5.5);
    pdf.setFont('helvetica', 'normal');
    pdf.text(ascii(`FY23-24 was ${prior.avgOverall.toFixed(2)}`), M + heroW - 3, y + 27.5, { align: 'right' });
  }

  // KPI grid — 4 cells to the right
  const kpiX = M + heroW + 6;
  const kpiW = (PW - kpiX - M - 6) / 2;
  const kpiH = (heroH - 4) / 2;
  const kpis = [
    { label: 'PROJECTS', value: String(p.n), sub: `${p.nScored} scored` },
    { label: 'BUDGET', value: `Rs ${p.totalBudget.toFixed(1)} Cr`, sub: `${p.funded} disclosed` },
    { label: 'GEOGRAPHIC REACH', value: `${p.states.length} state${p.states.length === 1 ? '' : 's'}`, sub: p.states.slice(0, 3).join(', ') },
    { label: 'TOP PROJECT', value: p.top?.code || '-', sub: `${p.top?.overall?.toFixed(2) || '-'} | ${p.top?.state || '-'}` },
  ];
  kpis.forEach((k, i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const kx = kpiX + col * (kpiW + 4);
    const ky = y + row * (kpiH + 4);
    setColor(pdf, BRAND.surface2, 'fill');
    pdf.roundedRect(kx, ky, kpiW, kpiH, 1.5, 1.5, 'F');
    setColor(pdf, BRAND.line, 'draw');
    pdf.setLineWidth(0.15);
    pdf.roundedRect(kx, ky, kpiW, kpiH, 1.5, 1.5, 'S');

    setColor(pdf, BRAND.ink4, 'text');
    pdf.setFontSize(5.5);
    pdf.setFont('helvetica', 'bold');
    pdf.text(ascii(k.label), kx + 3, ky + 4);

    setColor(pdf, BRAND.ink, 'text');
    pdf.setFontSize(11.5);
    pdf.setFont('helvetica', 'bold');
    pdf.text(truncate(pdf, k.value, kpiW - 6), kx + 3, ky + 9.5);

    setColor(pdf, BRAND.ink3, 'text');
    pdf.setFontSize(6.5);
    pdf.setFont('helvetica', 'normal');
    pdf.text(truncate(pdf, k.sub, kpiW - 6), kx + 3, ky + kpiH - 1.5);
  });

  y += heroH + 6;

  // ------------ CRITERIA BARS + TOOLS
  const colW = (PW - 2 * M - 6) / 2;

  // Left: 7 criteria bars
  const leftX = M;
  const bh = 44;
  setColor(pdf, [255, 255, 255], 'fill');
  pdf.roundedRect(leftX, y, colW, bh, 2, 2, 'F');
  setColor(pdf, BRAND.line, 'draw');
  pdf.setLineWidth(0.2);
  pdf.roundedRect(leftX, y, colW, bh, 2, 2, 'S');

  setColor(pdf, BRAND.ink, 'text');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8.5);
  pdf.text(ascii('OECD-DAC Criteria | this partner'), leftX + 3, y + 5);
  setColor(pdf, BRAND.ink4, 'text');
  pdf.setFontSize(5.5);
  pdf.setFont('helvetica', 'normal');
  pdf.text(
    ascii(prior ? 'grey=prog avg | light=FY23-24' : 'grey ticks = programme avg'),
    leftX + colW - 3, y + 5, { align: 'right' }
  );

  const barY0 = y + 9;
  const barRowH = 4.8;
  CRIT_ORDER.forEach((c, i) => {
    const by = barY0 + i * barRowH;
    setColor(pdf, BRAND.ink2, 'text');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(6.5);
    pdf.text(c, leftX + 3, by + 3);

    // Bar area — leave 26mm right padding for score + YoY delta side by side
    const rightPad = prior ? 26 : 16;
    const bx = leftX + 34, bw = colW - 34 - rightPad;
    drawBar(pdf, bx, by + 0.7, bw, 2.6, p.crits[c] || 0, 5, CRIT_COLOR[c]);

    // Prior-year ghost tick — light tinted colour of same criterion
    if (prior && prior.crits[c] != null) {
      const px = bx + (prior.crits[c] / 5) * bw;
      const cc = CRIT_COLOR[c];
      const light = [Math.round(cc[0] * 0.35 + 255 * 0.65), Math.round(cc[1] * 0.35 + 255 * 0.65), Math.round(cc[2] * 0.35 + 255 * 0.65)];
      setColor(pdf, light, 'draw');
      pdf.setLineWidth(0.9);
      pdf.line(px, by + 0.2, px, by + 3.7);
    }

    // Programme avg tick (grey, drawn last so it stays legible)
    const progAvg = prog.critAvg[c];
    if (progAvg != null) {
      const tx = bx + (progAvg / 5) * bw;
      setColor(pdf, BRAND.ink3, 'draw');
      pdf.setLineWidth(0.4);
      pdf.line(tx, by + 0.3, tx, by + 3.6);
    }

    // YoY delta (if prior year available) — sits left of the value
    if (prior && prior.crits[c] != null && p.crits[c] != null) {
      const yoy = p.crits[c] - prior.crits[c];
      setColor(pdf, yoy >= 0 ? BRAND.green : BRAND.red, 'text');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(6);
      pdf.text(
        ascii(`${yoy >= 0 ? '^' : 'v'}${yoy >= 0 ? '+' : ''}${yoy.toFixed(2)}`),
        leftX + colW - 12, by + 3, { align: 'right' }
      );
    }

    // Value label
    setColor(pdf, CRIT_COLOR[c], 'text');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.text((p.crits[c] || 0).toFixed(2), leftX + colW - 3, by + 3, { align: 'right' });
  });

  // Right: Tools + Recommendations
  const rightX = leftX + colW + 6;
  setColor(pdf, [255, 255, 255], 'fill');
  pdf.roundedRect(rightX, y, colW, bh, 2, 2, 'F');
  setColor(pdf, BRAND.line, 'draw');
  pdf.setLineWidth(0.2);
  pdf.roundedRect(rightX, y, colW, bh, 2, 2, 'S');

  setColor(pdf, BRAND.ink, 'text');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8.5);
  pdf.text(ascii('Thematic tools | where this partner operates'), rightX + 3, y + 5);

  const toolY0 = y + 9;
  const toolRowH = 5;
  ['NRM', 'H&H', 'SDLE', 'PoE'].forEach((tl, i) => {
    const ty = toolY0 + i * toolRowH;
    const val = p.tools[tl];
    setColor(pdf, BRAND.ink2, 'text');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(6.5);
    pdf.text(tl, rightX + 3, ty + 3);

    const bx = rightX + 22, bw = colW - 22 - 16;
    if (val != null) {
      drawBar(pdf, bx, ty + 0.7, bw, 2.8, val, 5, TOOL_COLOR[tl]);
      setColor(pdf, TOOL_COLOR[tl], 'text');
      pdf.setFontSize(7);
      pdf.text(val.toFixed(2), rightX + colW - 3, ty + 3, { align: 'right' });
    } else {
      setColor(pdf, BRAND.ink4, 'text');
      pdf.setFontSize(6.5);
      pdf.setFont('helvetica', 'italic');
      pdf.text('not applicable', bx, ty + 3);
    }
  });

  // Small divider in right box
  const divY = toolY0 + 4 * toolRowH + 2;
  setColor(pdf, BRAND.line, 'draw');
  pdf.setLineWidth(0.15);
  pdf.line(rightX + 3, divY, rightX + colW - 3, divY);

  setColor(pdf, BRAND.ink4, 'text');
  pdf.setFontSize(6);
  pdf.setFont('helvetica', 'bold');
  pdf.text(ascii('CAPITAL EFFICIENCY'), rightX + 3, divY + 4);
  const perPoint = p.totalBudget > 0 && p.avgOverall ? (p.totalBudget / (p.avgOverall * p.nScored)) : null;
  setColor(pdf, BRAND.ink, 'text');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text(perPoint != null ? ascii(`Rs ${perPoint.toFixed(2)} Cr / rubric-point`) : ascii('- (no disclosed budget)'), rightX + 3, divY + 9);
  setColor(pdf, BRAND.ink3, 'text');
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6);
  pdf.text(ascii('Comparative benchmark - see full methodology in the online dashboard.'), rightX + 3, divY + 12.5);

  y += bh + 6;

  // ------------ PROJECT PORTFOLIO TABLE
  const tblH = 60;
  setColor(pdf, [255, 255, 255], 'fill');
  pdf.roundedRect(M, y, PW - 2 * M, tblH, 2, 2, 'F');
  setColor(pdf, BRAND.line, 'draw');
  pdf.setLineWidth(0.2);
  pdf.roundedRect(M, y, PW - 2 * M, tblH, 2, 2, 'S');

  setColor(pdf, BRAND.ink, 'text');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8.5);
  pdf.text(ascii('Project portfolio | ranked'), M + 3, y + 5);
  setColor(pdf, BRAND.ink4, 'text');
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6.5);
  pdf.text(ascii(`${p.nScored} scored | ${p.n} total`), PW - M - 3, y + 5, { align: 'right' });

  // Table header
  const tblY0 = y + 10;
  const cols = [
    { label: 'Code', w: 20 },
    { label: 'Project name', w: 78 },
    { label: 'State', w: 30 },
    { label: 'Budget', w: 22, align: 'right' },
    { label: 'Score', w: 18, align: 'right' },
    { label: 'Band', w: 18 },
  ];
  let cx = M + 3;
  setColor(pdf, BRAND.ink4, 'text');
  pdf.setFontSize(5.5);
  pdf.setFont('helvetica', 'bold');
  cols.forEach((c) => {
    pdf.text(c.label.toUpperCase(), c.align === 'right' ? cx + c.w - 2 : cx, tblY0, { align: c.align || 'left' });
    cx += c.w;
  });

  setColor(pdf, BRAND.line, 'draw');
  pdf.setLineWidth(0.15);
  pdf.line(M + 3, tblY0 + 2, PW - M - 3, tblY0 + 2);

  // Table rows
  const rowH = 5.2;
  const maxRows = Math.min(p.projects.length, 9);
  for (let i = 0; i < maxRows; i++) {
    const pr = p.projects[i];
    const ry = tblY0 + 6 + i * rowH;

    if (i % 2 === 1) {
      setColor(pdf, BRAND.surface2, 'fill');
      pdf.rect(M + 3, ry - 3, PW - 2 * M - 6, rowH, 'F');
    }

    let x2 = M + 3;
    setColor(pdf, BRAND.ink, 'text');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.text(pr.code, x2, ry);
    x2 += cols[0].w;

    setColor(pdf, BRAND.ink2, 'text');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(6.5);
    pdf.text(truncate(pdf, pr.name || '-', cols[1].w - 2), x2, ry);
    x2 += cols[1].w;

    pdf.text(truncate(pdf, pr.state || '-', cols[2].w - 2), x2, ry);
    x2 += cols[2].w;

    pdf.text(pr.budgetCr > 0 ? ascii(`Rs ${pr.budgetCr.toFixed(1)} Cr`) : '-', x2 + cols[3].w - 2, ry, { align: 'right' });
    x2 += cols[3].w;

    const b = bandOf(pr.overall);
    setColor(pdf, bandColor(b), 'text');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.text(pr.overall != null ? pr.overall.toFixed(2) : '-', x2 + cols[4].w - 2, ry, { align: 'right' });
    x2 += cols[4].w;

    // Compact band pill fits within 18mm column width
    if (b) drawBandPill(pdf, x2, ry - 3.2, b, true);
  }

  if (p.projects.length > maxRows) {
    setColor(pdf, BRAND.ink4, 'text');
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(6);
    pdf.text(ascii(`+ ${p.projects.length - maxRows} more project${p.projects.length - maxRows === 1 ? '' : 's'} - full list in the online dashboard`), M + 3, y + tblH - 3);
  }

  y += tblH + 6;

  // ------------ RECOMMENDATIONS
  const recs = buildRecommendations(p, prog, prior);
  const recH = 30;

  setColor(pdf, [4, 42, 84, 5], 'fill');
  pdf.setFillColor(240, 245, 250);
  pdf.roundedRect(M, y, PW - 2 * M, recH, 2, 2, 'F');
  setColor(pdf, BRAND.blue, 'draw');
  pdf.setLineWidth(0.3);
  pdf.rect(M, y, 1.5, recH, 'F');

  setColor(pdf, BRAND.navy, 'text');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.text(ascii('BOARD RECOMMENDATIONS'), M + 5, y + 5);
  setColor(pdf, BRAND.ink4, 'text');
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6);
  pdf.text(ascii('auto-generated from partner vs programme profile | reviewable, not prescriptive'), PW - M - 3, y + 5, { align: 'right' });

  recs.forEach((r, i) => {
    setColor(pdf, BRAND.accent, 'fill');
    pdf.circle(M + 6, y + 10.5 + i * 6.5, 0.9, 'F');
    setColor(pdf, BRAND.ink2, 'text');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    const wrapped = pdf.splitTextToSize(ascii(r), PW - 2 * M - 12);
    pdf.text(wrapped, M + 9, y + 11 + i * 6.5);
  });

  y += recH + 4;

  // ------------ FOOTER
  setColor(pdf, BRAND.line, 'draw');
  pdf.setLineWidth(0.15);
  pdf.line(M, PH - 14, PW - M, PH - 14);

  setColor(pdf, BRAND.ink4, 'text');
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6);
  pdf.text(ascii('Auto-generated by the HDFC Bank Parivartan Impact Dashboard | Independent OECD-DAC evaluation | FY 2024-25 | 186 CSR projects'), M, PH - 9);
  pdf.text(ascii('Scores computed live from 638k+ survey responses. Bands: Excellent >= 4.6 | Good 3.6-4.5 | Moderate 2.6-3.5 | Poor <= 2.5. All figures in Rs Crores.'), M, PH - 6);

  // Filename
  const safeName = p.partner.replace(/[^a-zA-Z0-9]+/g, '_').slice(0, 60);
  pdf.save(`Parivartan_Scorecard_${safeName}.pdf`);
}
